import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { Button, Loading } from '@thirdspacelearning/library';
import { QUERY_STATUS } from '@tsl-frontend/tsl-utilities';

import { ProgrammeForm } from '../programme-details';
import ProgrammesTable from './ProgrammesTable';

import { ProgrammeType } from '../../types';

import { getProgrammes } from '@api';
import { CurriculumQueryKeys } from '@services';
import { ErrorActionPanel, PageLayout, TabsPanel } from '@shared';
import { Country } from '@types';
import { countryTabList } from '@utils';

const ProgrammesPage = () => {
    const [showCreateProgramme, setShowCreateProgramme] = useState(false);
    const [selectedTab, setSelectedTab] = useState<Country>(Country.GB);

    const { status, data: programmes } = useQuery<ProgrammeType[]>({
        queryKey: [`${CurriculumQueryKeys.PROGRAMMES}`, selectedTab],
        queryFn: () => getProgrammes(selectedTab),
        retry: 1,
        refetchOnWindowFocus: false,
    });

    const handleCreateProgramme = () => {
        setShowCreateProgramme(true);
    };

    return (
        <PageLayout
            title="Programmes"
            buttons={[
                <Button
                    key="programme-connections"
                    component={Link}
                    to="/programmes/connections"
                    variant="outlined"
                    color="primary"
                >
                    Programme Connections
                </Button>,
                ...(programmes
                    ? [
                          <Button
                              key="create-new-programme"
                              variant="contained"
                              color="primary"
                              onClick={handleCreateProgramme}
                              data-testid="create-programme-button"
                          >
                              Create New Programme
                          </Button>,
                      ]
                    : []),
            ]}
        >
            <TabsPanel<Country>
                selectedTab={selectedTab}
                tabList={countryTabList}
                setSelectedTab={(selectedTab: Country) => {
                    setSelectedTab(selectedTab);
                }}
            />

            {status === QUERY_STATUS.pending && (
                <Loading loadingMessage="Loading programmes..." />
            )}

            {status === QUERY_STATUS.error && (
                <ErrorActionPanel
                    error="There was an error loading the programmes"
                    message="Please refresh the page and try again. If the problem persists, please let us know."
                />
            )}

            {status === QUERY_STATUS.success && programmes && (
                <>
                    <ProgrammesTable
                        programmes={programmes}
                        country={selectedTab}
                    />

                    <ProgrammeForm
                        open={showCreateProgramme}
                        onClose={setShowCreateProgramme}
                    />
                </>
            )}
        </PageLayout>
    );
};

export default ProgrammesPage;
